import {
  applyCopy,
  applyWords,
  type Descriptions,
  EDITABLE_WORDS,
  fullRow,
  LANG_NAMES,
  type Layout,
  type PostSound,
  type PublicWall,
  type PublicWallPost,
  playsMuted,
  postsNeeded,
  WALL_LANGS,
  type Wall,
  type WallCopyPatch,
  type WallDesign,
  type WallLang,
  type WallPreview as WallPreviewData,
  type WallSound,
  type WallText,
  type WallTiles,
  type WordOverrides,
  wallTextFor,
  wallWords,
} from "@brandwall/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { ApiError, api, patch, post } from "../api";
import { HeadlineAndText } from "../components/HeadlineAndText";
import { PostLabels, useLabels } from "../components/PostLabels";
import { useWorkspace } from "../components/Shell";
import { useToast } from "../components/Toast";
import { FullPreview, WallPreview } from "../components/WallPreview";

interface Embed {
  snippet: string;
  steps: string[];
  shopify: { steps: string[]; code: string };
  wordpress: { steps: string[]; code: string };
}

/** Video and sound on a wall (D-092). */
const SOUNDS: { id: WallSound; label: string; text: string }[] = [
  {
    id: "no_songs",
    label: "Play sound, but not songs",
    text: "Videos play with their own sound: voices, the moment itself. Videos with a licensed song from Instagram play without sound, and so do videos where Brandwall can't tell.",
  },
  { id: "all", label: "Play all sound", text: "Every video plays with its sound, songs included." },
  { id: "none", label: "No sound", text: "Every video on this wall plays without sound." },
];

/** Video tiles (D-094, D-101). */
const TILES: { id: WallTiles; label: string; text: string }[] = [
  {
    id: "play",
    label: "Play on mouse-over",
    text: "A video tile plays silently while the mouse is on it, and pauses when it leaves. On phones a tap opens the post. A small play icon shows it's a video.",
  },
  {
    id: "still",
    label: "Show a still picture",
    text: "Video tiles show their picture until opened.",
  },
];

const layouts: { id: Layout; label: string }[] = [
  { id: "grid", label: "Grid" },
  { id: "carousel", label: "Carousel" },
  { id: "masonry", label: "Masonry" },
  { id: "strip", label: "Strip" },
];

function Stepper({
  label,
  value,
  min,
  max,
  onChange,
  unit = "",
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  unit?: string;
  onChange: (v: number) => void;
}) {
  return (
    <div className="row">
      <span className="spacer" style={{ fontWeight: 600 }}>
        {label}
      </span>
      <div className="stepper">
        <button
          type="button"
          aria-label={`Less ${label.toLowerCase()}`}
          onClick={() => onChange(Math.max(min, value - 1))}
        >
          −
        </button>
        <span style={{ minWidth: 46, textAlign: "center" }} aria-live="polite">
          {value}
          {unit}
        </span>
        <button
          type="button"
          aria-label={`More ${label.toLowerCase()}`}
          onClick={() => onChange(Math.min(max, value + 1))}
        >
          +
        </button>
      </div>
    </div>
  );
}

/** A change to one post on the wall, kept until "Save changes". */
interface PostOp {
  postId: string;
  body: { size?: number; hidden?: boolean; place?: number | null; sound?: PostSound | null };
}

/** The wall's posts with the unsaved changes applied, in the order they were made. */
/** The words as they'd be saved: trimmed, empty ones gone. */
function trimText(t: WallText): WallText {
  const copy: WallText["copy"] = {};
  for (const [l, own] of Object.entries(t.copy) as [WallLang, Record<string, string>][]) {
    const kept = Object.fromEntries(
      Object.entries(own ?? {})
        .map(([k, v]) => [k, v.trim()])
        .filter(([, v]) => v),
    );
    if (Object.keys(kept).length) copy[l] = kept;
  }
  return { ...t, buttonUrl: t.buttonUrl.trim(), copy };
}

/** Which posts a wall shows (D-100): all, or only some labels; and buttons for visitors. */
function WhichPosts(p: {
  wall: Wall;
  labels: string[];
  buttons: boolean;
  setLabels: (l: string[]) => void;
  setButtons: (b: boolean) => void;
  disabled: boolean;
}) {
  const all = useLabels().data?.labels ?? [];
  const some = p.labels.length > 0;
  return (
    <fieldset className="card stack" disabled={p.disabled} style={{ margin: 0 }}>
      <legend style={{ fontWeight: 700, padding: 0 }}>Which posts</legend>
      {p.wall.selection === "picked" ? (
        <p className="small muted">This wall shows the posts you chose for it.</p>
      ) : (
        <>
          <div className="stack" role="radiogroup" aria-label="Which posts" style={{ gap: 8 }}>
            <label className="row" style={{ gap: 8 }}>
              <input type="radio" name="which" checked={!some} onChange={() => p.setLabels([])} />
              <b>All posts</b>
            </label>
            <label className="row" style={{ gap: 8 }}>
              <input
                type="radio"
                name="which"
                checked={some}
                disabled={all.length === 0}
                onChange={() => all[0] && p.setLabels([all[0].id])}
              />
              <b>Only posts labelled</b>
            </label>
          </div>
          {some && (
            <div className="row wrap" style={{ gap: 10, paddingLeft: 24 }}>
              {all.map((l) => (
                <label key={l.id} className="row" style={{ gap: 4 }}>
                  <input
                    type="checkbox"
                    checked={p.labels.includes(l.id)}
                    onChange={(e) =>
                      p.setLabels(
                        e.target.checked
                          ? [...p.labels, l.id]
                          : p.labels.filter((x) => x !== l.id).length
                            ? p.labels.filter((x) => x !== l.id)
                            : p.labels,
                      )
                    }
                  />
                  {l.name}
                </label>
              ))}
            </div>
          )}
          {all.length === 0 && (
            <p className="small muted">
              Make labels on the Sources page and label posts in your Inbox to show only some.
            </p>
          )}
        </>
      )}
      <label className="row" style={{ gap: 8 }}>
        <input
          type="checkbox"
          checked={p.buttons}
          onChange={(e) => p.setButtons(e.target.checked)}
        />
        <span className="stack" style={{ gap: 2 }}>
          <b>Show labels as buttons for visitors</b>
          <span className="small muted">
            "All" and a button per label above the posts. A visitor taps one to see only those
            posts.
          </span>
        </span>
      </label>
      {all.length > 0 && (
        <p className="small muted" style={{ margin: 0 }}>
          One wall can show a different label on each page: add it to the code on that page, like{" "}
          <code>data-label="{all[0]!.slug}"</code> next to <code>data-brandwall</code>. Save to see
          a change in the preview.
        </p>
      )}
    </fieldset>
  );
}

/** The wall's words with the unsaved changes applied. */
function draftText(
  saved: WallText,
  d: (Partial<Omit<WallText, "copy">> & { copy?: WallCopyPatch }) | null,
): WallText {
  if (!d) return saved;
  const { copy, ...fields } = d;
  return { ...saved, ...fields, copy: applyCopy(saved.copy, copy ?? {}) };
}

function applyOps(posts: PublicWallPost[], ops: PostOp[]) {
  let list = [...posts];
  for (const { postId, body } of ops) {
    const i = list.findIndex((p) => p.id === postId);
    if (i < 0) continue;
    const p = { ...list[i]! };
    if (body.hidden) {
      list = list.filter((x) => x.id !== postId);
      continue;
    }
    if (body.size) p.size = body.size;
    if (body.place === null) p.fixed = false;
    list[i] = p;
    if (typeof body.place === "number") {
      list.splice(i, 1);
      // One post per place: a post that held this place moves with the others again.
      list = list.map((x, j) => (j === body.place && x.fixed ? { ...x, fixed: false } : x));
      list.splice(Math.min(body.place, list.length), 0, { ...p, fixed: true });
    }
  }
  return list;
}

/** A video's own sound setting with the unsaved changes: "on", "off" or null (the wall's). */
function soundOf(data: WallPreviewData | undefined, postId: string, ops: PostOp[]) {
  let s: PostSound | null = data?.sound.posts[postId]?.sound ?? null;
  for (const op of ops) if (op.postId === postId && op.body.sound !== undefined) s = op.body.sound;
  return s;
}

/** Whether a video plays muted with the unsaved choices (D-092). */
function mutedWith(
  data: WallPreviewData | undefined,
  sound: WallSound | null,
  postId: string,
  ops: PostOp[],
) {
  return playsMuted(
    sound ?? "no_songs",
    soundOf(data, postId, ops),
    data?.sound.posts[postId]?.song ?? null,
  );
}

export function WallEditor() {
  const { ws, id } = useParams();
  const workspace = useWorkspace();
  const canEdit = workspace.role !== "viewer";
  const qc = useQueryClient();
  const toast = useToast();
  const wallQ = useQuery({
    queryKey: ["wall", ws, id],
    queryFn: () => api<Wall>(`/v1/w/${ws}/walls/${id}`),
  });
  // Language (D-097): undefined is no unsaved change; null follows the website.
  const [langDraft, setLangDraft] = useState<WallLang | null | undefined>(undefined);
  // Only the words typed in, per language: "" puts back the usual word. Saved as a change, so
  // words someone else changed meanwhile stay.
  const [wordsDraft, setWordsDraft] = useState<WordOverrides | undefined>(undefined);
  // The language the wording is edited in, and the preview shown in.
  const [lang, setLang] = useState<WallLang | null>(null);
  const fixedLang = langDraft !== undefined ? langDraft : (wallQ.data?.language ?? null);
  const viewLang: WallLang | null = fixedLang ?? lang ?? wallQ.data?.languages[0] ?? null;
  const previewQ = useQuery({
    queryKey: ["wall-preview", ws, id, viewLang],
    enabled: Boolean(viewLang),
    placeholderData: (prev) => prev,
    queryFn: () =>
      api<WallPreviewData>(`/v1/w/${ws}/walls/${id}/preview?lang=${encodeURIComponent(viewLang!)}`),
  });
  // A post's description being written in the post panel.
  // The headline and text (D-098): only what changed, and the words typed per language.
  const [textDraft, setTextDraft] = useState<
    (Partial<Omit<WallText, "copy">> & { copy?: WallCopyPatch; dismissSuggestion?: boolean }) | null
  >(null);
  // Descriptions typed in the post panel, per post and language ("postId:lang"); saved with the
  // other changes.
  const [descDrafts, setDescDrafts] = useState<Record<string, string>>({});
  // Design (how the wall looks) and Settings (what it does), with the preview on both.
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "settings" ? "settings" : "design";
  // The unsaved "Video and sound" choice; null follows the saved wall, so a change made elsewhere
  // (an approval, a teammate, an undo) shows here and is never saved back over.
  const [soundDraft, setSoundDraft] = useState<WallSound | null>(null);
  const sound = soundDraft ?? wallQ.data?.sound ?? null;
  const [tilesDraft, setTilesDraft] = useState<WallTiles | null>(null);
  // "Which posts" (D-100): undefined is no unsaved change.
  const [labelsDraft, setLabelsDraft] = useState<string[] | undefined>(undefined);
  const [buttonsDraft, setButtonsDraft] = useState<boolean | undefined>(undefined);
  // "When there are too few posts" (D-095): undefined is no unsaved change.
  const [minDraft, setMinDraft] = useState<number | null | undefined>(undefined);
  // What's typed in the number field, so it can be empty or half-typed for a moment.
  const [minText, setMinText] = useState<string | null>(null);
  // Until the wall has loaded, the preview keeps what the server sent.
  const tiles = tilesDraft ?? wallQ.data?.tiles ?? null;
  const embedQ = useQuery({
    queryKey: ["wall-embed", ws, id],
    queryFn: () => api<Embed>(`/v1/w/${ws}/walls/${id}/embed`),
  });
  const [draft, setDraft] = useState<{ layout: Layout; design: WallDesign; name: string } | null>(
    null,
  );
  const [device, setDevice] = useState<"desktop" | "phone">("desktop");
  const [full, setFull] = useState(false);
  const [platform, setPlatform] = useState<"code" | "shopify" | "wordpress">("code");
  const [pages, setPages] = useState("");
  const [copied, setCopied] = useState(false);
  // Changes are a draft until "Save changes" (D-067): the design, and what was done to posts.
  const [designDirty, setDesignDirty] = useState(false);
  const [ops, setOps] = useState<PostOp[]>([]);
  const [saving, setSaving] = useState(false);
  const soundDirty =
    (soundDraft !== null && soundDraft !== wallQ.data?.sound) ||
    (tilesDraft !== null && tilesDraft !== wallQ.data?.tiles);
  const minDirty = minDraft !== undefined && minDraft !== wallQ.data?.minPosts;
  const whichDirty =
    (labelsDraft !== undefined &&
      JSON.stringify([...labelsDraft].sort()) !==
        JSON.stringify([...(wallQ.data?.labels ?? [])].sort())) ||
    (buttonsDraft !== undefined && buttonsDraft !== wallQ.data?.labelButtons);
  const langDirty =
    (langDraft !== undefined && langDraft !== wallQ.data?.language) ||
    Object.entries(wordsDraft ?? {}).some(([l, own]) =>
      Object.entries(own ?? {}).some(
        ([k, v]) =>
          v.trim() !== ((wallQ.data?.words[l as WallLang] as Record<string, string>)?.[k] ?? ""),
      ),
    );
  const savedDescription = (postId: string, l: WallLang) =>
    (previewQ.data?.descriptions[postId] as Descriptions | undefined)?.[l] ?? null;
  // Descriptions typed in the post panel and not saved yet.
  const descChanges = Object.entries(descDrafts).flatMap(([k, text]) => {
    const [postId, l] = k.split(":") as [string, WallLang];
    return text.trim() !== (savedDescription(postId, l)?.text ?? "") ? [{ postId, l, text }] : [];
  });
  const descDirty = descChanges.length > 0;
  // Changed for real: a box ticked and unticked again isn't a change.
  const textDirty =
    Boolean(textDraft?.dismissSuggestion) ||
    (textDraft !== null &&
      wallQ.data !== undefined &&
      JSON.stringify(trimText(draftText(wallQ.data.text, textDraft))) !==
        JSON.stringify(trimText(wallQ.data.text)));
  const buttonUrl = textDraft?.buttonUrl?.trim() ?? "";
  const badButtonUrl = buttonUrl !== "" && !/^https?:\/\/\S+$/i.test(buttonUrl);
  const dirty =
    designDirty ||
    soundDirty ||
    minDirty ||
    langDirty ||
    descDirty ||
    textDirty ||
    whichDirty ||
    ops.length > 0;
  // The post being changed in the preview, and where its menu sits in the preview frame.
  const frameRef = useRef<HTMLDivElement>(null);
  const [menu, setMenu] = useState<{ post: PublicWallPost; left: number; top: number } | null>(
    null,
  );
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setMenu(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Leaving with unsaved changes asks first: closing the tab, and links inside the app.
  useEffect(() => {
    if (!dirty) return;
    const onUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    const onClick = (e: MouseEvent) => {
      const a = (e.target as Element).closest?.("a[href]");
      if (!a || a.getAttribute("target") === "_blank" || e.metaKey || e.ctrlKey) return;
      if (!window.confirm("You have unsaved changes to this wall. Leave without saving them?")) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("beforeunload", onUnload);
    document.addEventListener("click", onClick, true);
    return () => {
      window.removeEventListener("beforeunload", onUnload);
      document.removeEventListener("click", onClick, true);
    };
  }, [dirty]);

  const editPost = (target: PublicWallPost, tile: HTMLElement) => {
    const f = frameRef.current?.getBoundingClientRect();
    const r = tile.getBoundingClientRect();
    if (!f) return;
    const width = 250;
    setMenu({
      post: target,
      left: Math.max(8, Math.min(r.left - f.left, f.width - width - 8)),
      top: r.top - f.top + Math.min(r.height / 2, 120),
    });
  };
  const changePost = (body: PostOp["body"]) => {
    if (!menu) return;
    setOps((o) => [...o, { postId: menu.post.id, body }]);
    setMenu(
      body.hidden
        ? null
        : {
            ...menu,
            post: {
              ...menu.post,
              size: body.size ?? menu.post.size,
              fixed: body.place === undefined ? menu.post.fixed : body.place !== null,
              muted:
                body.sound === undefined
                  ? menu.post.muted
                  : mutedFor(menu.post.id, [...ops, { postId: menu.post.id, body }]),
            },
          },
    );
  };
  const saveDescription = async (postId: string, l: WallLang, text: string) => {
    try {
      const r = await patch<{ summary: string }>(`/v1/w/${ws}/posts/${postId}/description`, {
        lang: l,
        text: text.trim() || null,
      });
      toast(r.summary);
      setDescDrafts(({ [`${postId}:${l}`]: _, ...rest }) => rest);
      await qc.invalidateQueries({ queryKey: ["wall-preview", ws, id] });
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "Couldn't save the description.");
    }
  };
  const movePost = (target: PublicWallPost, index: number) => {
    setMenu(null);
    setOps((o) => [...o, { postId: target.id, body: { place: index } }]);
  };

  useEffect(() => {
    if (wallQ.data && !draft) {
      setDraft({ layout: wallQ.data.layout, design: wallQ.data.design, name: wallQ.data.name });
      setPages(wallQ.data.pages.join("\n"));
    }
  }, [wallQ.data, draft]);

  const postSound = (postId: string, list: PostOp[] = ops) => soundOf(previewQ.data, postId, list);
  const mutedFor = (postId: string, list: PostOp[] = ops) =>
    mutedWith(previewQ.data, sound, postId, list);

  const update = (next: Partial<{ layout: Layout; design: Partial<WallDesign>; name: string }>) => {
    if (!draft) return;
    setDraft({ ...draft, ...next, design: { ...draft.design, ...(next.design ?? {}) } });
    setDesignDirty(true);
  };

  const save = async () => {
    if (!draft || saving) return false;
    // Checked before anything is saved, so a save never stops halfway.
    if (badButtonUrl) {
      toast("The button's link needs a full web address, starting with https://.");
      return false;
    }
    setSaving(true);
    try {
      if (textDirty && textDraft) await patch(`/v1/w/${ws}/walls/${id}/text`, textDraft);
      if (designDirty)
        await patch(`/v1/w/${ws}/walls/${id}`, {
          name: draft.name,
          layout: draft.layout,
          // Only what changed here, so a change made elsewhere meanwhile (an approved headline,
          // say) stays.
          design: wallQ.data
            ? Object.fromEntries(
                Object.entries(draft.design).filter(
                  ([k, v]) =>
                    JSON.stringify(v) !== JSON.stringify(wallQ.data!.design[k as keyof WallDesign]),
                ),
              )
            : draft.design,
        });
      if (minDirty) await patch(`/v1/w/${ws}/walls/${id}/settings`, { minPosts: minDraft });
      if (whichDirty)
        await patch(`/v1/w/${ws}/walls/${id}/settings`, {
          labels: labelsDraft,
          labelButtons: buttonsDraft,
        });
      if (langDirty)
        await patch(`/v1/w/${ws}/walls/${id}/settings`, {
          language: langDraft,
          words: wordsDraft,
        });
      if (soundDirty)
        await post(`/v1/w/${ws}/walls/${id}/sound`, {
          sound: soundDraft ?? undefined,
          tiles: tilesDraft ?? undefined,
        });
      for (const op of ops) await post(`/v1/w/${ws}/walls/${id}/posts/${op.postId}`, op.body);
      for (const c of descChanges)
        await patch(`/v1/w/${ws}/posts/${c.postId}/description`, {
          lang: c.l,
          text: c.text.trim() || null,
        });
      setDesignDirty(false);
      setSoundDraft(null);
      setTilesDraft(null);
      setMinDraft(undefined);
      setLangDraft(undefined);
      setWordsDraft(undefined);
      setDescDrafts({});
      setTextDraft(null);
      setLabelsDraft(undefined);
      setButtonsDraft(undefined);
      setOps([]);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["wall-preview", ws, id] }),
        qc.invalidateQueries({ queryKey: ["wall", ws, id] }),
        qc.invalidateQueries({ queryKey: ["walls", ws] }),
      ]);
      toast(`Saved your changes to ${draft.name}.`);
      return true;
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "Couldn't save your changes.");
      return false;
    } finally {
      setSaving(false);
    }
  };
  const discard = () => {
    if (!wallQ.data) return;
    setDraft({ layout: wallQ.data.layout, design: wallQ.data.design, name: wallQ.data.name });
    setSoundDraft(null);
    setTilesDraft(null);
    setMinDraft(undefined);
    setLangDraft(undefined);
    setWordsDraft(undefined);
    setDescDrafts({});
    setTextDraft(null);
    setLabelsDraft(undefined);
    setButtonsDraft(undefined);
    setDesignDirty(false);
    setOps([]);
    setMenu(null);
  };

  // The preview shows the draft: the design, and the post changes not saved yet.
  const preview = useMemo<PublicWall | null>(
    () =>
      previewQ.data && draft
        ? {
            ...previewQ.data,
            layout: draft.layout,
            design: draft.design,
            tiles: tiles ?? previewQ.data.tiles,
            // The headline and text as they would be saved, in the language shown.
            text: wallQ.data
              ? wallTextFor(
                  draft.design.heading,
                  draftText(wallQ.data.text, textDraft),
                  previewQ.data.lang,
                  wallQ.data.brandLanguage,
                  wallWords(previewQ.data.lang, applyWords(wallQ.data.words, wordsDraft ?? {})),
                  previewQ.data.tag,
                )
              : previewQ.data.text,
            // The wording as it would be saved, in the language shown.
            words: wallWords(
              previewQ.data.lang,
              applyWords(wallQ.data?.words ?? {}, wordsDraft ?? {}),
            ) as unknown as Record<string, string>,
            posts: applyOps(previewQ.data.posts, ops).map((p) => {
              if (!previewQ.data?.sound.posts[p.id]) return p;
              const muted = mutedWith(previewQ.data, sound, p.id, ops);
              // Without Brandwall's copy, a muted video shows as its picture, as on the wall.
              if (!p.video)
                return {
                  ...p,
                  type: muted ? ("image" as const) : ("video" as const),
                  muted: false,
                };
              return { ...p, muted };
            }),
          }
        : null,
    [previewQ.data, draft, ops, sound, tiles, wordsDraft, wallQ.data, textDraft],
  );

  const publish = async () => {
    if (dirty && !(await save())) return;
    try {
      const r = await post<Wall & { result: { summary: string } }>(
        `/v1/w/${ws}/walls/${id}/publish`,
        {
          pages: pages
            .split("\n")
            .map((p) => p.trim())
            .filter(Boolean),
        },
      );
      toast(r.result.summary);
      qc.invalidateQueries({ queryKey: ["wall", ws, id] });
      qc.invalidateQueries({ queryKey: ["walls", ws] });
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "Couldn't publish.");
    }
  };
  const unpublish = async () => {
    await post(`/v1/w/${ws}/walls/${id}/unpublish`);
    toast(
      "Your wall is off your site. The code can stay; it shows nothing until you publish again.",
    );
    qc.invalidateQueries({ queryKey: ["wall", ws, id] });
  };
  const copy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  if (!wallQ.data || !draft) return <p className="muted">Loading…</p>;
  const wall = wallQ.data;
  const d = draft.design;
  const row = fullRow(draft.layout, d.columns);
  const minSetting = minDraft !== undefined ? minDraft : wall.minPosts;
  const need = postsNeeded(minSetting, draft.layout, d.columns);
  const embed = embedQ.data;
  const code = embed
    ? platform === "shopify"
      ? embed.shopify
      : platform === "wordpress"
        ? embed.wordpress
        : { steps: embed.steps, code: embed.snippet }
    : null;

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="row wrap">
        <Link to={`/w/${ws}/walls`} className="small muted">
          Walls
        </Link>
        <span className="muted">›</span>
        <input
          className="input"
          style={{ maxWidth: 280, fontWeight: 700 }}
          value={draft.name}
          disabled={!canEdit}
          aria-label="Wall name"
          onChange={(e) => update({ name: e.target.value })}
        />
        <span className={`pill ${wall.status === "live" ? "ok" : ""}`}>
          {wall.status === "live" ? (
            <>
              <span className="dot" /> Live
            </>
          ) : (
            "Draft"
          )}
        </span>
        {wall.hidden && wall.status === "live" && (
          <span className="small">
            <span className="pill warn">
              Hidden: {wall.hidden.have} of {wall.hidden.need} posts needed
            </span>{" "}
            <Link to={`/w/${ws}/discover`}>Find more in Discover</Link>
          </span>
        )}
      </div>

      <div className="editor">
        <div className="stack">
          <div className="tabs" role="tablist">
            {(
              [
                ["design", "Design"],
                ["settings", "Settings"],
              ] as const
            ).map(([t, label]) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={tab === t}
                className={`tab ${tab === t ? "on" : ""}`}
                onClick={() => setParams(t === "design" ? {} : { tab: t }, { replace: true })}
              >
                {label}
              </button>
            ))}
          </div>
          {tab === "settings" ? (
            <>
              <fieldset className="card stack" disabled={!canEdit} style={{ margin: 0 }}>
                <legend style={{ fontWeight: 700, padding: 0 }}>Video and sound</legend>
                <div
                  className="stack"
                  role="radiogroup"
                  aria-label="Video and sound"
                  style={{ gap: 10 }}
                >
                  {SOUNDS.map((o) => (
                    <label key={o.id} className="row" style={{ alignItems: "flex-start", gap: 8 }}>
                      <input
                        type="radio"
                        name="sound"
                        checked={sound === o.id}
                        onChange={() => setSoundDraft(o.id)}
                        style={{ marginTop: 4 }}
                      />
                      <span className="stack" style={{ gap: 2 }}>
                        <b>{o.label}</b>
                        <span className="small muted">{o.text}</span>
                      </span>
                    </label>
                  ))}
                </div>
                {sound === "all" && (
                  <p className="small" role="note">
                    Songs added on Instagram are licensed for Instagram. Playing them on your site
                    is your choice.
                  </p>
                )}
                <p className="small muted">
                  Visitors can't switch the sound on for a video that plays without it. You can
                  change one video's sound by clicking it in the preview.
                </p>
                <div
                  className="stack"
                  role="radiogroup"
                  aria-label="Videos in tiles"
                  style={{ gap: 10 }}
                >
                  <b>Videos in tiles</b>
                  {TILES.map((o) => (
                    <label key={o.id} className="row" style={{ alignItems: "flex-start", gap: 8 }}>
                      <input
                        type="radio"
                        name="tiles"
                        checked={tiles === o.id}
                        onChange={() => setTilesDraft(o.id)}
                        style={{ marginTop: 4 }}
                      />
                      <span className="stack" style={{ gap: 2 }}>
                        <b>{o.label}</b>
                        <span className="small muted">{o.text}</span>
                      </span>
                    </label>
                  ))}
                  <p className="small muted">
                    Tiles never play sound. Visitors who ask their device for less motion see still
                    pictures, and so do videos that play in Instagram's player.
                  </p>
                </div>
              </fieldset>
              <fieldset className="card stack" disabled={!canEdit} style={{ margin: 0 }}>
                <legend style={{ fontWeight: 700, padding: 0 }}>Language</legend>
                <div className="stack" role="radiogroup" aria-label="Language" style={{ gap: 10 }}>
                  <label className="row" style={{ alignItems: "flex-start", gap: 8 }}>
                    <input
                      type="radio"
                      name="language"
                      checked={fixedLang === null}
                      onChange={() => setLangDraft(null)}
                      style={{ marginTop: 4 }}
                    />
                    <span className="stack" style={{ gap: 2 }}>
                      <b>Follow my website</b>
                      <span className="small muted">
                        The wall speaks the language of each page it's on: Danish pages get a Danish
                        wall, English pages an English one. Pages in other languages get English.
                      </span>
                    </span>
                  </label>
                  <label className="row" style={{ alignItems: "center", gap: 8 }}>
                    <input
                      type="radio"
                      name="language"
                      checked={fixedLang !== null}
                      onChange={() => setLangDraft(viewLang ?? "en")}
                    />
                    <b>Always use</b>
                    <select
                      className="input"
                      style={{ width: "auto" }}
                      aria-label="The wall's language"
                      value={fixedLang ?? viewLang ?? "en"}
                      onChange={(e) => setLangDraft(e.target.value as WallLang)}
                    >
                      {WALL_LANGS.map((l) => (
                        <option key={l} value={l}>
                          {LANG_NAMES[l]}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                {fixedLang === null && wall.language === null && wall.seenIn.length > 0 && (
                  <p className="small muted">
                    Your pages show it in {wall.seenIn.map((l) => LANG_NAMES[l]).join(", ")}.
                  </p>
                )}
                <div className="stack" style={{ gap: 8 }}>
                  <div className="row wrap" style={{ gap: 8 }}>
                    <b className="spacer">Wording</b>
                    {fixedLang === null && (
                      <select
                        className="input"
                        style={{ width: "auto" }}
                        aria-label="Wording in"
                        value={viewLang ?? "en"}
                        onChange={(e) => setLang(e.target.value as WallLang)}
                      >
                        {WALL_LANGS.map((l) => (
                          <option key={l} value={l}>
                            {LANG_NAMES[l]}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <span className="small muted">
                    Your own words for the buttons and links, in {LANG_NAMES[viewLang ?? "en"]}.
                    Leave one empty for the usual words. The preview shows them.
                  </span>
                  {EDITABLE_WORDS.map(([key, label]) => {
                    const l = viewLang ?? "en";
                    const own = wordsDraft?.[l]?.[key] ?? wall.words[l]?.[key] ?? "";
                    return (
                      <label key={key} className="field">
                        <span className="small">{label}</span>
                        <input
                          className="input"
                          maxLength={40}
                          value={own}
                          placeholder={wallWords(l)[key]}
                          onChange={(e) => {
                            const value = e.target.value;
                            setWordsDraft((all) => ({
                              ...all,
                              [l]: { ...(all?.[l] ?? {}), [key]: value },
                            }));
                          }}
                        />
                      </label>
                    );
                  })}
                </div>
              </fieldset>
              <WhichPosts
                wall={wall}
                labels={labelsDraft ?? wall.labels}
                buttons={buttonsDraft ?? wall.labelButtons}
                setLabels={setLabelsDraft}
                setButtons={setButtonsDraft}
                disabled={!canEdit}
              />
              <fieldset className="card stack" disabled={!canEdit} style={{ margin: 0 }}>
                <legend style={{ fontWeight: 700, padding: 0 }}>
                  When there are too few posts
                </legend>
                <label className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <input
                    type="checkbox"
                    checked={need > 0}
                    onChange={(e) => setMinDraft(e.target.checked ? null : 0)}
                  />
                  <span>Hide the wall when it has fewer than</span>
                  <input
                    className="input"
                    type="number"
                    min={1}
                    max={48}
                    style={{ width: 70 }}
                    aria-label="Fewest posts"
                    disabled={need === 0}
                    value={minText ?? String(need || row)}
                    onChange={(e) => {
                      setMinText(e.target.value);
                      const n = Number(e.target.value);
                      if (Number.isInteger(n) && n >= 1 && n <= 48) setMinDraft(n);
                    }}
                    onBlur={() => setMinText(null)}
                  />
                  <span>posts</span>
                </label>
                <p className="small muted">
                  {wall.kind === "product"
                    ? "On product pages this counts each product's own posts: a product with too few shows no wall."
                    : "While hidden, the wall takes no room on your page, and it comes back by itself once it has enough posts."}{" "}
                  The default is one full row: {row} for this layout.
                  {minSetting !== null && need !== row && (
                    <>
                      {" "}
                      <button
                        type="button"
                        className="btn link small"
                        onClick={() => setMinDraft(null)}
                      >
                        Use one full row
                      </button>
                    </>
                  )}
                </p>
              </fieldset>
            </>
          ) : (
            <fieldset className="card stack" disabled={!canEdit} style={{ margin: 0 }}>
              <legend className="sr-only">Design</legend>
              <div className="stack" style={{ gap: 8 }}>
                <span style={{ fontWeight: 600 }}>Layout</span>
                <div className="seg" role="group" aria-label="Layout">
                  {layouts.map((l) => (
                    <button
                      key={l.id}
                      type="button"
                      aria-pressed={draft.layout === l.id}
                      onClick={() => update({ layout: l.id })}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>
              {(draft.layout === "grid" || draft.layout === "masonry") && (
                <>
                  <div className="row">
                    <div className="spacer">
                      <div style={{ fontWeight: 600 }}>Keep adding posts as visitors scroll</div>
                      <div className="muted small">
                        The wall fills the screen, then shows more posts as people scroll down.
                      </div>
                    </div>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={d.infinite}
                        aria-label="Keep adding posts as visitors scroll"
                        onChange={(e) => update({ design: { infinite: e.target.checked } })}
                      />
                      <span />
                    </label>
                  </div>
                  {!d.infinite && (
                    <Stepper
                      label="Rows"
                      value={d.rows}
                      min={1}
                      max={6}
                      onChange={(rows) => update({ design: { rows } })}
                    />
                  )}
                  <Stepper
                    label="Columns"
                    value={d.columns}
                    min={2}
                    max={6}
                    onChange={(columns) => update({ design: { columns } })}
                  />
                </>
              )}
              <Stepper
                label="Corners"
                value={d.corners}
                min={0}
                max={24}
                unit=" px"
                onChange={(corners) => update({ design: { corners } })}
              />
              <Stepper
                label="Spacing"
                value={d.spacing}
                min={0}
                max={32}
                unit=" px"
                onChange={(spacing) => update({ design: { spacing } })}
              />
              <div className="row wrap">
                {(["background", "text", "accent"] as const).map((k) => (
                  <label key={k} className="field" style={{ textTransform: "capitalize" }}>
                    {k}
                    <input
                      type="color"
                      value={d[k]}
                      onChange={(e) => update({ design: { [k]: e.target.value } })}
                    />
                  </label>
                ))}
              </div>
              {workspace.brand.colors.length > 0 && (
                <div className="stack" style={{ gap: 6 }}>
                  <span className="small muted">
                    Colours from {workspace.website ?? "your site"}
                  </span>
                  <div className="row">
                    {workspace.brand.colors.map((c) => (
                      <button
                        key={c}
                        type="button"
                        className="swatch"
                        style={{ background: c, cursor: "pointer" }}
                        aria-label={`Use ${c} as accent`}
                        onClick={() => update({ design: { accent: c } })}
                      />
                    ))}
                  </div>
                </div>
              )}
              <label className="field">
                Font
                <input
                  className="input"
                  value={d.font}
                  onChange={(e) => update({ design: { font: e.target.value } })}
                  placeholder="inherit"
                />
                <span className="small muted">"inherit" uses your site's own font.</span>
              </label>
            </fieldset>
          )}
          {tab === "design" && (
            <HeadlineAndText
              wall={wall}
              heading={d.heading}
              setHeading={(heading) => update({ design: { heading } })}
              text={draftText(wall.text, textDraft)}
              draft={textDraft}
              setDraft={setTextDraft}
              lang={viewLang ?? wall.brandLanguage}
              setLang={fixedLang === null ? setLang : null}
              tag={previewQ.data?.tag ?? { account: null, hashtag: null }}
              disabled={!canEdit}
              badButtonUrl={badButtonUrl}
              dismiss={async () => {
                try {
                  await patch(`/v1/w/${ws}/walls/${id}/text`, { dismissSuggestion: true });
                  await qc.invalidateQueries({ queryKey: ["wall", ws, id] });
                } catch (err) {
                  toast(
                    err instanceof ApiError ? err.message : "That didn't work. Please try again.",
                  );
                }
              }}
            />
          )}
        </div>

        <div className="stack">
          <div className="row">
            <h2 className="spacer">Live preview</h2>
            {preview && preview.posts.length > 0 && (
              <button type="button" className="btn secondary sm" onClick={() => setFull(true)}>
                Preview
              </button>
            )}
            {full && preview && (
              <FullPreview
                wall={preview}
                phone={device === "phone"}
                onClose={() => setFull(false)}
              />
            )}
            <div className="seg" role="group" aria-label="Preview size">
              <button
                type="button"
                aria-pressed={device === "desktop"}
                onClick={() => setDevice("desktop")}
              >
                Desktop
              </button>
              <button
                type="button"
                aria-pressed={device === "phone"}
                onClick={() => setDevice("phone")}
              >
                Phone
              </button>
            </div>
          </div>
          {canEdit && preview && preview.posts.length > 0 && (
            <p className="small muted">
              Drag posts to arrange them. Click a post to make it bigger or hide it. Preview shows
              the wall as visitors see it.
            </p>
          )}
          <div
            ref={frameRef}
            className={`preview-frame ${device === "phone" ? "phone" : ""}`}
            style={{ position: "relative" }}
          >
            {menu && (
              <div
                className="tile-menu"
                role="dialog"
                aria-label="Change this post on the wall"
                style={{ left: menu.left, top: menu.top }}
              >
                <div className="row">
                  <b className="spacer">
                    {menu.post.creator ? `@${menu.post.creator}` : "This post"}
                  </b>
                  <button
                    type="button"
                    className="btn link small"
                    aria-label="Close"
                    onClick={() => setMenu(null)}
                  >
                    ✕
                  </button>
                </div>
                {draft.layout === "grid" ? (
                  <>
                    <span className="small muted">Size on this wall</span>
                    <div className="seg" role="group" aria-label="Size">
                      {[1, 2, 3].map((n) => (
                        <button
                          key={n}
                          type="button"
                          aria-pressed={(menu.post.size ?? 1) === n}
                          onClick={() => changePost({ size: n })}
                        >
                          {n}×{n}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <span className="small muted">Bigger posts work with the Grid layout.</span>
                )}
                {menu.post.fixed ? (
                  <span className="small">
                    Stays in this place.{" "}
                    <button
                      type="button"
                      className="btn link small"
                      onClick={() => changePost({ place: null })}
                    >
                      Let it move with new posts
                    </button>
                  </span>
                ) : (
                  <span className="small muted">Drag a post to keep it in a place.</span>
                )}
                {previewQ.data?.sound.posts[menu.post.id] && (
                  <>
                    <span className="small muted">Sound</span>
                    <div className="seg" role="group" aria-label="Sound">
                      {(
                        [
                          [null, "As the wall"],
                          ["on", "On"],
                          ["off", "Off"],
                        ] as const
                      ).map(([v, label]) => (
                        <button
                          key={label}
                          type="button"
                          aria-pressed={postSound(menu.post.id) === v}
                          onClick={() => changePost({ sound: v })}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                {viewLang &&
                  (() => {
                    const key = `${menu.post.id}:${viewLang}`;
                    const saved = savedDescription(menu.post.id, viewLang);
                    const text = descDrafts[key] ?? saved?.text ?? "";
                    return (
                      <div className="stack" style={{ gap: 4 }}>
                        <label className="stack" style={{ gap: 4 }}>
                          <span className="small muted">
                            What it shows, in {LANG_NAMES[viewLang]}
                          </span>
                          <textarea
                            className="input"
                            rows={3}
                            maxLength={300}
                            value={text}
                            placeholder="Not written yet. Until then, screen readers hear the caption."
                            onChange={(e) => {
                              const value = e.target.value;
                              setDescDrafts((d) => ({ ...d, [key]: value }));
                            }}
                          />
                        </label>
                        <span className="small muted">
                          {saved?.by === "brand"
                            ? "Written by you. "
                            : saved
                              ? "Written by Brandwall's AI. "
                              : ""}
                          Screen readers hear it, followed by who posted it. It's the same on all
                          your walls, and saved with your other changes.
                        </span>
                        {saved?.by === "brand" && descDrafts[key] === undefined && (
                          <button
                            type="button"
                            className="btn link small"
                            style={{ justifySelf: "start" }}
                            onClick={() => void saveDescription(menu.post.id, viewLang, "")}
                          >
                            Let Brandwall write it
                          </button>
                        )}
                      </div>
                    );
                  })()}
                <PostLabels
                  p={{ id: menu.post.id, labels: previewQ.data?.postLabels[menu.post.id] ?? [] }}
                  canAct={canEdit}
                />
                <button
                  type="button"
                  className="btn link small"
                  style={{ justifySelf: "start" }}
                  onClick={() => changePost({ hidden: true })}
                >
                  Hide on this wall
                </button>
              </div>
            )}
            {preview && preview.posts.length > 0 ? (
              <WallPreview
                wall={preview}
                onEdit={canEdit ? editPost : undefined}
                onMove={canEdit ? movePost : undefined}
              />
            ) : (
              <p className="muted" style={{ textAlign: "center", padding: 30 }}>
                No live posts yet. Approve posts with permission in your{" "}
                <Link to={`/w/${ws}/inbox`}>inbox</Link>, or add trusted accounts.
              </p>
            )}
          </div>
        </div>
      </div>

      <section className="card stack">
        <div>
          <h2>Publish {draft.name}</h2>
          <p className="muted">
            Add it to your site once. After that it updates by itself every time a post is approved.
          </p>
        </div>
        <div className="seg" role="group" aria-label="Platform">
          <button
            type="button"
            aria-pressed={platform === "code"}
            onClick={() => setPlatform("code")}
          >
            Embed code
          </button>
          <button
            type="button"
            aria-pressed={platform === "shopify"}
            onClick={() => setPlatform("shopify")}
          >
            Shopify
          </button>
          <button
            type="button"
            aria-pressed={platform === "wordpress"}
            onClick={() => setPlatform("wordpress")}
          >
            WordPress
          </button>
        </div>
        {code && (
          <>
            <ol className="steps-list">
              {code.steps.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ol>
            <div className="code">{code.code}</div>
            <div>
              <button type="button" className="btn secondary sm" onClick={() => copy(code.code)}>
                {copied ? "✓ Copied" : "Copy code"}
              </button>
            </div>
          </>
        )}
        <label className="field">
          Show it on (one page address per line, for your own overview)
          <textarea
            className="input"
            rows={2}
            value={pages}
            disabled={!canEdit}
            onChange={(e) => setPages(e.target.value)}
            placeholder={workspace.website ?? "https://yourshop.com/"}
          />
        </label>
        {canEdit && (
          <div className="row">
            {wall.status === "live" && (
              <button type="button" className="btn secondary" onClick={unpublish}>
                Take it off my site
              </button>
            )}
            <span className="spacer" />
            <button type="button" className="btn" onClick={publish}>
              {wall.status === "live" ? "Publish changes" : "Publish"}
            </button>
          </div>
        )}
      </section>
      {canEdit && dirty && (
        <div className="savebar" role="status">
          <span>
            Unsaved changes
            {wall.status === "live" ? ". Your live wall changes when you save." : "."}
          </span>
          <button type="button" className="btn link small" onClick={discard} disabled={saving}>
            Discard
          </button>
          <button type="button" className="btn" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      )}
    </div>
  );
}
